import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { HealthProvider } from "@/hooks/useHealth";
import { MusicProvider } from "@/hooks/useMusicSettings";
import { CosmeticsProvider } from "@/hooks/useCosmetics";
import { SoundtrackProvider } from "@/hooks/useSoundtracks";
import { useProgressSync } from "@/hooks/useProgressSync";
import GamePage from "@/pages/GamePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import SetupNamePage from "@/pages/SetupNamePage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import LevelMapPage from "@/pages/LevelMapPage";
import ProfilePage from "@/pages/ProfilePage";
import ChallengesPage from "@/pages/ChallengesPage";
import AdminPage from "@/pages/AdminPage";
import LandingPage from "@/pages/LandingPage";
import LegalPage from "@/pages/LegalPage";
import FAQPage from "@/pages/FAQPage";
import HelpCenterPage from "@/pages/HelpCenterPage";
import InventoryPage from "@/pages/InventoryPage";
import CodexPage from "@/pages/CodexPage";
import CraftingPage from "@/pages/CraftingPage";
import GuildPage from "@/pages/GuildPage";
import StoryPage from "@/pages/StoryPage";
import TournamentPage from "@/pages/TournamentPage";
import GameNavBar from "@/components/game/GameNavBar";
import { Toaster } from "@/components/ui/sonner";

// Progress loader component - loads progress from server when authenticated
function ProgressLoader({ children }) {
  const { isAuthenticated, syncEnabled } = useAuth();
  const { loadProgress } = useProgressSync();

  useEffect(() => {
    if (isAuthenticated && syncEnabled) {
      loadProgress();
    }
  }, [isAuthenticated, syncEnabled, loadProgress]);

  return children;
}

// Protected route wrapper
function ProtectedRoute({ children, hideNav = false }) {
  const { isAuthenticated, hasPlayerName, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen game-gradient-bg flex items-center justify-center">
        <div className="text-center">
          <span className="text-6xl animate-bounce inline-block">🍌</span>
          <p className="font-display text-xl text-foreground mt-4">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!hasPlayerName) {
    return <Navigate to="/setup-name" replace />;
  }
  
  return (
    <>
      <div className={hideNav ? "" : "pb-14"}>{children}</div>
      {!hideNav && <GameNavBar />}
    </>
  );
}

// Public route - redirect to level map if already logged in
function PublicRoute({ children, redirectToMap = true }) {
  const { isAuthenticated, hasPlayerName, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen game-gradient-bg flex items-center justify-center">
        <div className="text-center">
          <span className="text-6xl animate-bounce inline-block">🍌</span>
          <p className="font-display text-xl text-foreground mt-4">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (redirectToMap && isAuthenticated && hasPlayerName) {
    return <Navigate to="/map" replace />;
  }
  
  return children;
}

// Setup name route - only accessible if logged in but no name
function SetupNameRoute({ children }) {
  const { isAuthenticated, hasPlayerName, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen game-gradient-bg flex items-center justify-center">
        <div className="text-center">
          <span className="text-6xl animate-bounce inline-block">🍌</span>
          <p className="font-display text-xl text-foreground mt-4">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (hasPlayerName) {
    return <Navigate to="/map" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Landing page - public */}
      <Route 
        path="/" 
        element={
          <PublicRoute redirectToMap={true}>
            <LandingPage />
          </PublicRoute>
        } 
      />
      
      {/* Public routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        } 
      />
      
      {/* Setup name route */}
      <Route 
        path="/setup-name" 
        element={
          <SetupNameRoute>
            <SetupNamePage />
          </SetupNameRoute>
        } 
      />
      
      {/* Protected routes */}
      <Route 
        path="/map" 
        element={
          <ProtectedRoute>
            <LevelMapPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/levels" 
        element={
          <ProtectedRoute>
            <LevelMapPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/play/:levelId" 
        element={
          <ProtectedRoute hideNav={true}>
            <GamePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/play" 
        element={<Navigate to="/play/1" replace />}
      />
      <Route 
        path="/leaderboard" 
        element={
          <ProtectedRoute>
            <LeaderboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/challenges" 
        element={
          <ProtectedRoute>
            <ChallengesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/inventory" 
        element={
          <ProtectedRoute>
            <InventoryPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Panel - no auth required, handled internally */}
      <Route path="/admin" element={<AdminPage />} />
      
      {/* Legal Pages */}
      <Route path="/terms" element={<LegalPage page="terms" />} />
      <Route path="/privacy" element={<LegalPage page="privacy" />} />
      <Route path="/cookies" element={<LegalPage page="cookies" />} />
      
      {/* Support Pages */}
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/codex" element={<ProtectedRoute><CodexPage /></ProtectedRoute>} />
      <Route path="/crafting" element={<ProtectedRoute><CraftingPage /></ProtectedRoute>} />
      <Route path="/guild" element={<ProtectedRoute><GuildPage /></ProtectedRoute>} />
      <Route path="/story" element={<ProtectedRoute><StoryPage /></ProtectedRoute>} />
      <Route path="/tournament" element={<ProtectedRoute><TournamentPage /></ProtectedRoute>} />
      <Route path="/help" element={<HelpCenterPage />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Hide Emergent branding via JavaScript (safer than CSS, won't affect modals)
  useEffect(() => {
    const hideEmergentBranding = () => {
      // Find and hide any element containing "emergent" text at the bottom of the page
      const allElements = document.querySelectorAll('a, div, span');
      allElements.forEach(el => {
        const text = el.textContent?.toLowerCase() || '';
        const href = el.getAttribute('href')?.toLowerCase() || '';
        
        // Check if it's the Emergent branding (usually at bottom, contains "emergent" or "made with")
        if ((text.includes('emergent') || text.includes('made with') || href.includes('emergent')) &&
            !el.closest('[role="dialog"]') && // Don't hide anything in modals
            !el.closest('.App') && // Don't hide app content
            !el.closest('#root')) { // Don't hide root content
          el.style.display = 'none';
        }
      });
    };

    // Run immediately and on DOM changes
    hideEmergentBranding();
    
    // Watch for dynamically added elements
    const observer = new MutationObserver(hideEmergentBranding);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AuthProvider>
          <HealthProvider>
            <MusicProvider>
              <SoundtrackProvider>
                <CosmeticsProvider>
                  <ProgressLoader>
                    <AppRoutes />
                  </ProgressLoader>
                </CosmeticsProvider>
              </SoundtrackProvider>
            </MusicProvider>
          </HealthProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
